import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type {
  DealValues,
  RegistryDealSetup,
  RegistryOfferDocument,
} from '../../api/registryDealTypes';

const reviewRows: { label: string; keys: string[] }[] = [
  { label: 'Title', keys: ['offer.title'] },
  { label: 'Class and issue price', keys: ['class.name', 'class.issuePrice'] },
  { label: 'Offer link', keys: ['offer.url'] },
  { label: 'Closes', keys: ['offer.closeDate', 'offer.closeTime', 'offer.timezone'] },
  { label: 'Documents', keys: ['offer.documents'] },
  { label: 'Shown to existing investors', keys: ['offer.useExisting'] },
  { label: 'Minimum, maximum, increment', keys: ['offer.min', 'offer.max', 'offer.increment'] },
  { label: 'Payable now per unit', keys: ['offer.payableNow'] },
  { label: 'Payment methods', keys: ['offer.payEft', 'offer.payBpay'] },
  { label: 'Payment due', keys: ['offer.payDueMode', 'offer.payDays', 'offer.payDate'] },
  { label: 'Applications account', keys: ['bank.accountName', 'bank.bsb', 'bank.account'] },
  { label: 'Application alerts', keys: ['notify.newApplication'] },
];

function displayValue(value: string | boolean | undefined): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return value || '—';
}

function documentTitles(documents: RegistryOfferDocument[]): string {
  return documents.length ? documents.map((document) => document.title).join('\n') : '—';
}

export function ReviewRows({
  values,
  documents = [],
}: {
  values: DealValues;
  documents?: RegistryOfferDocument[];
}) {
  return (
    <Box
      component="dl"
      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '12rem 1fr' }, gap: 1, mb: 2 }}
    >
      {reviewRows.map((row) => (
        <React.Fragment key={row.label}>
          <Typography component="dt" fontWeight={600}>
            {row.label}
          </Typography>
          <Typography component="dd" sx={{ m: 0, whiteSpace: 'pre-line' }}>
            {row.keys
              .filter((key) => key !== 'offer.payDays' || values['offer.payDueMode'] !== 'DATE')
              .filter((key) => key !== 'offer.payDate' || values['offer.payDueMode'] === 'DATE')
              .map((key) =>
                key === 'offer.documents' ? documentTitles(documents) : displayValue(values[key]),
              )
              .join(' · ')}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

type SetupFund = RegistryDealSetup['fund'];

function bankMessage(fund: NonNullable<SetupFund>): string {
  if (fund.bankVerified) return 'Trust bank details verified by a second person.';
  if (fund.bankVerificationPending) {
    return 'Trust bank verification is waiting for a second person in Approvals.';
  }
  return 'Trust bank details need second-person verification before publication.';
}

export function ActiveFundAlerts({ fund }: { fund: SetupFund }) {
  if (!fund) return null;
  return (
    <>
      <Alert severity="success" sx={{ mb: 2 }}>
        Active fund: {fund.legalName}
      </Alert>
      <Alert severity={fund.bankVerified ? 'success' : 'warning'} sx={{ mb: 2 }}>
        {bankMessage(fund)}
      </Alert>
    </>
  );
}

export function BankVerificationButton({
  fund,
  disabled,
  onRequest,
}: {
  fund: SetupFund;
  disabled: boolean;
  onRequest: () => void;
}) {
  if (!fund || fund.bankVerified) return null;
  return (
    <Button
      variant="contained"
      disabled={disabled || fund.bankVerificationPending}
      onClick={onRequest}
    >
      {fund.bankVerificationPending ? 'Bank verification pending' : 'Request bank verification'}
    </Button>
  );
}

export function OfferStatusAlert({ offer }: { offer: RegistryDealSetup['offer'] }) {
  if (offer?.status === 'PENDING_APPROVAL') {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Waiting for a second person&apos;s approval.
      </Alert>
    );
  }
  if (offer?.status !== 'PUBLISHED') return null;
  return (
    <>
      <Alert severity="success" sx={{ mb: 2 }}>
        Offer published. The fund opportunity is Application open.
      </Alert>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Button
          variant="contained"
          component={RouterLink}
          to={`/manager/offers/${offer.externalId}`}
        >
          Open offer
        </Button>
        <Button variant="outlined" component={RouterLink} to="/manager/offers">
          Offers
        </Button>
        <Button variant="outlined" component={RouterLink} to="/manager/applications">
          Applications
        </Button>
      </Stack>
    </>
  );
}
